import { useEffect, useState } from "react";
import { Sparkles, ImageOff, Loader2, Check, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

const abs = (u) => (u?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export default function MoodBoard({ q, setQ, canEdit }) {
  const mb = q.mood_board || {};
  const mv = q.mood_board_variants || { status: "none", styles: [] };
  const [styles, setStyles] = useState([]);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/mood-board/styles").then((r) => setStyles(r.data)).catch(() => {}); }, []);
  const src = abs(mb.url);

  const explore = async () => {
    setBusy("explore");
    try { setQ((await api.post(`/quotes/${q.id}/mood-board/variations`)).data); toast.success("Painting three styles — about 30 seconds"); }
    catch (e) { toast.error(formatErr(e)); }
    setBusy("");
  };
  const pick = async (k) => {
    setBusy(k);
    try { setQ((await api.post(`/quotes/${q.id}/mood-board/select`, { style: k })).data); toast.success("Mood board updated — it will appear in your PDF too"); }
    catch (e) { toast.error(formatErr(e)); }
    setBusy("");
  };

  const showStrip = mv.status === "generating" || (mv.status === "ready" && mv.tier === q.tier);
  return (
    <div data-testid="mood-board-section">
      <div className="relative rounded-3xl overflow-hidden aspect-[16/7] bg-brand-sand" data-testid="mood-board">
        {mb.status === "generating" || !src ? (
          <div className="absolute inset-0 shimmer" data-testid="mood-board-loading">
            <div className="absolute inset-0 grid place-items-center">
              <div className="text-center">
                <Sparkles className="w-6 h-6 text-brand-accent mx-auto animate-pulse" />
                <p className="text-xs uppercase tracking-[0.25em] text-brand-muted mt-3">Painting your {q.tier_info.label} mood board…</p>
              </div>
            </div>
          </div>
        ) : (
          <img src={src} alt={`${q.tier_info.label} mood board`} className="absolute inset-0 w-full h-full object-cover fade-up" data-testid="mood-board-image" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/70 via-brand-ink/10 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 flex flex-wrap items-end justify-between gap-4 text-brand-cream">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-brand-line/80 inline-flex items-center gap-2">
              {mb.status === "fallback" ? <><ImageOff className="w-3 h-3" /> Curated style reference</> : <><Sparkles className="w-3 h-3" /> AI concept mood board{mb.style_label ? ` · ${mb.style_label}` : ""}</>}
            </p>
            <p className="font-serif-display text-3xl md:text-5xl mt-1" data-testid="mood-board-package">{q.tier_info.label} package</p>
          </div>
          {canEdit && (
            <button onClick={explore} disabled={!!busy || mv.status === "generating" || mb.status === "generating"} data-testid="explore-styles-btn"
              className="inline-flex items-center gap-2 rounded-full bg-brand-cream/15 hover:bg-brand-cream/25 backdrop-blur-md border border-brand-cream/30 px-5 py-2.5 text-sm transition-colors disabled:opacity-50">
              {busy === "explore" || mv.status === "generating" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Explore 3 styles
            </button>
          )}
        </div>
      </div>

      {showStrip && (
        <div className="grid sm:grid-cols-3 gap-4 mt-4" data-testid="mood-board-variants">
          {styles.map((s) => {
            const ready = mv.styles?.includes(s.key);
            const active = mb.style === s.key && mb.status === "ready";
            return (
              <div key={s.key} className={`relative rounded-2xl overflow-hidden aspect-[16/9] border-2 ${active ? "border-brand-accent" : "border-transparent"} bg-brand-sand`} data-testid={`variant-${s.key}`}>
                {ready ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${q.id}/mood-board/variants/${s.key}?r=${mv.run || 0}`} alt={s.label} className="absolute inset-0 w-full h-full object-cover fade-up" data-testid={`variant-image-${s.key}`} />
                ) : mv.status === "generating" ? (
                  <div className="absolute inset-0 shimmer" data-testid={`variant-loading-${s.key}`} />
                ) : (
                  <div className="absolute inset-0 grid place-items-center text-xs text-brand-muted">Not available</div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/70 to-transparent pointer-events-none" />
                <div className="absolute bottom-0 left-0 right-0 p-4 flex items-center justify-between text-brand-cream">
                  <p className="font-serif-display text-xl">{s.label}</p>
                  {ready && (active ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] bg-brand-accent text-white px-3 py-1 rounded-full" data-testid={`variant-selected-${s.key}`}><Check className="w-3 h-3" /> Selected</span>
                  ) : (
                    <button onClick={() => pick(s.key)} disabled={!!busy} data-testid={`variant-use-${s.key}`}
                      className="text-xs rounded-full bg-brand-cream/20 hover:bg-brand-accent border border-brand-cream/30 px-3 py-1.5 transition-colors inline-flex items-center gap-1">
                      {busy === s.key && <Loader2 className="w-3 h-3 animate-spin" />} Use this
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
